const $$splitComponentImporter = () => import('/app/frontend/src/routes/_app/fleet-management/contracts/index.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';
import { createSearchValidator } from '@/lib/search-validators';
import { CONTRACT_SORT_KEYS, CONTRACT_TABS } from '@/features/fleet-management/contract/model/contract-columns';
type ContractFilters = {
  status?: string;
  hasVehicle?: string;
  nextEndBucket?: string;
};
const FILTER_KEYS: (keyof ContractFilters)[] = ['status', 'hasVehicle', 'nextEndBucket'];

/**
 * Parses the vehicles column's facet ('assigned'/'not-assigned', see its
 * `paramKey: 'hasVehicle'`) into the boolean the API takes. Anything else
 * (a hand-edited URL) is treated as "no filter", same policy as splitFilter.
 */

// Purchase's PRD-specified default ("purchase date" descending) has no
// dedicated field yet — Task 1 modeled no per-type date, so this stands in
// for it (flagged, not silently renamed — see the plan's Risks note).
// Leasing/insurance sort by next end ascending (Section 1) — a virtual field
// with no backing column, fully supported by the backend (ContractSortField
// in contract-vocabulary.ts); nulls (no known end) always sort last
// regardless of direction, handled server-side.

/**
 * Splits a multiselect filter from its URL form into the array the API
 * takes (see vehicles' route). Unlike vehicles' version, this one requires
 * an explicit allowed-value set and drops anything outside it — a
 * hand-edited URL's `status=bogus` would otherwise reach the API typed as a
 * valid status and 400 (review finding, MR !131).
 */

const baseSearchValidator = createSearchValidator<ContractFilters, ContractTab>({
  tabs: CONTRACT_TABS,
  filterKeys: FILTER_KEYS,
  sortKeys: CONTRACT_SORT_KEYS
});
interface ContractColumnsSearchParams {
  purchaseColumns?: string;
  leasingColumns?: string;
  insuranceColumns?: string;
}

// Each tab's own `<tab>Columns` param (Section 1: "each tab keeps its own
// column selection") lives outside the shared filter/sort validator — it
// isn't a backend filter, just per-tab UI state that also makes a copied
// link reproduce every tab's column choice, not only the active one.
// Explicitly typed with `?` (not inferred from the object literal below) —
// an inferred type keeps these keys' values as `string | undefined` but not
// optional, which TanStack Router's search-param typing reads as required,
// breaking every unrelated `navigate({ to: '/fleet-management/contracts' })`
// call that doesn't specify them.
function validateContractSearch(search: Record<string, unknown>): SearchParamsWithFilters<ContractFilters, ContractTab> & ContractColumnsSearchParams {
  return {
    ...baseSearchValidator(search),
    purchaseColumns: typeof search.purchaseColumns === 'string' ? search.purchaseColumns : undefined,
    leasingColumns: typeof search.leasingColumns === 'string' ? search.leasingColumns : undefined,
    insuranceColumns: typeof search.insuranceColumns === 'string' ? search.insuranceColumns : undefined
  };
}
export const Route = createFileRoute('/_app/fleet-management/contracts/')({
  validateSearch: validateContractSearch,
  staticData: {
    breadCrumb: 'Contracts'
  },
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});